import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { Badge } from "@/components/ui/badge";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { ArrowUpRight } from "@/icons";

//commented and added by sudip
// import { projects } from "@/data/portfolio.data";
import { caseStudies } from "@/data/caseStudies.data";
import { ProjectPreview } from "../portfolio/ProjectPreview";
import { ChevronRight } from "@/icons";

/** Concept project mockup — a layered browser + phone frame tinted per project. */
// function ProjectMockup({ accent }: { accent: string }) {
//   return (
//     <div className="relative h-56 overflow-hidden rounded-t-[1.25rem]">
//       <div
//         className="absolute inset-0"
//         style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}05)` }}
//       />
//       <div className="absolute inset-0 bg-grid opacity-40" />
//       {/* Browser frame */}
//       <div className="absolute left-8 top-8 right-8 overflow-hidden rounded-t-xl border border-border bg-card shadow-[0_24px_60px_-30px_rgba(15,23,42,0.4)]">
//         <div className="flex items-center gap-1.5 border-b border-border bg-background/70 px-3 py-2">
//           <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
//           <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
//           <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
//         </div>
//         <div className="space-y-2 p-4">
//           <div className="h-3 w-1/3 rounded-full" style={{ background: `${accent}88` }} />
//           <div className="h-2 w-3/4 rounded-full bg-muted" />
//           <div className="h-2 w-2/3 rounded-full bg-muted" />
//           <div className="mt-3 grid grid-cols-3 gap-2">
//             {[0, 1, 2].map((i) => (
//               <div key={i} className="h-10 rounded-lg bg-muted" />
//             ))}
//           </div>
//         </div>
//       </div>
//       {/* Phone frame */}
//       <div className="absolute -bottom-2 right-6 hidden w-16 overflow-hidden rounded-xl border border-border bg-card p-1 shadow-lg sm:block">
//         <div className="rounded-lg p-2" style={{ background: accent }}>
//           <div className="h-1.5 w-2/3 rounded-full bg-white/70" />
//           <div className="mt-1.5 h-1 w-full rounded-full bg-white/40" />
//           <div className="mt-1 h-1 w-3/4 rounded-full bg-white/40" />
//         </div>
//       </div>
//     </div>
//   );
// }
const selectedCategories = ["Enterprise Platform", "Healthcare Platform", "Business Platform", "E-commerce"]
const someCaseStudies = caseStudies.filter(caseItem => selectedCategories.includes(caseItem.category));

/** Portfolio — realistic concept projects with device mockups + tech badges. */
export function Portfolio() {
  return (
    <Section id="portfolio" className="bg-card/40">
      <Container>
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Featured Projects"
            title="Software Solutions Built for Real Business Challenges"
            description="Explore representative projects that demonstrate how we solve complex business challenges through AI, automation and enterprise software."
          />
          <CtaButton to="/portfolio" variant="outline" withArrow className="shrink-0">
            View all work
          </CtaButton>
        </div>

        <motion.div
          variants={staggerContainer(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-12 grid gap-6 md:grid-cols-2"
        >
          {someCaseStudies && someCaseStudies.map((p) => (
            <motion.article
              key={p.slug}
              variants={fadeUp}
              data-testid={`project-card-${p.slug}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-border bg-card transition-all duration-500 hover:-translate-y-2 hover:border-primary/20 hover:shadow-[0_40px_90px_-35px_rgba(15,23,42,0.35)]"
              // className="group flex flex-col overflow-hidden rounded-[1.25rem] border border-border bg-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_70px_-32px_rgba(15,23,42,0.3)]"
            >
              {/* <ProjectMockup accent={p.accent} /> */}
              <ProjectPreview industry={p.industry} accent={p.accent} />

              <div className="relative">

                <div
                  className="absolute inset-0"
                  style={{background: `linear-gradient(135deg, ${p.accent}22, ${p.accent}08)`,}}
                />

                <div className="absolute inset-0 bg-grid opacity-40" />

                <div className="flex flex-1 flex-col relative my-5 mx-5 rounded-2xl border border-border bg-card p-6 shadow-sm">

                  {/* Category */}

                  <div className="flex items-center justify-between">

                    <div className="flex flex-wrap gap-2">

                      <Badge
                        // variant="secondary"
                        className="rounded-full border-0 text-white"
                        style={{backgroundColor: p.accent,}}
                      >
                        {p.industry}
                      </Badge>

                      <Badge
                        // variant="outline"
                        className="rounded-full"
                        style={{color: p.accent, borderColor: p.accent,  backgroundColor: `${p.accent}12`,}}
                      >
                        {p.category}
                      </Badge>

                    </div>

                  </div>

                  {/* Title */}

                  <h3
                    className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground transition-colors group-hover:text-primary" >
                    {p.title}
                  </h3>

                  {/* Summary */}

                  <p className="mt-3 text-sm leading-7 text-muted-foreground">
                    {p.summary}
                  </p>

                  {/* Business Outcome */}

                  {/* <div className="mt-6 flex items-start gap-3" > */}
                  <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4" >

                    <div className="text-center text-xs font-bold uppercase tracking-wider text-muted-foreground" >
                      Business Outcome
                    </div>

                    <div className="mt-2 text-sm font-medium" style={{color: p.accent,}} >
                      {p.businessOutcome}
                    </div>

                  </div>

                  {/* Technologies */}

                  <div className="mt-5 flex flex-wrap gap-2" >

                    {p.tech
                      .slice(0, 3)
                      .map((tech) => (

                        <Badge
                          key={tech}
                          variant="secondary"
                          className="rounded-full"
                        >
                          {tech}
                        </Badge>

                      ))}

                    {p.tech.length > 3 && (

                      <Badge variant="outline" className="rounded-full" >
                        +{p.tech.length - 3}
                      </Badge>

                    )}

                  </div>

                  {/* CTA */}

                  <Link

                    to={`/case-studies/${p.slug}`}

                    className="mt-6 inline-flex items-center gap-2 font-semibold text-primary transition-all duration-300 group-hover:gap-3" >

                    Explore Case Study

                    <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />

                  </Link>

                </div>
              </div>

              {/* <div className="flex flex-1 flex-col p-7">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                  {p.category}
                </span>
                <h3 className="mt-2 font-heading text-2xl font-bold text-foreground">
                  {p.title}
                </h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {p.summary}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.tech.map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="rounded-full bg-muted font-medium text-muted-foreground"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
                <Link
                  to="/case-studies"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-300 group-hover:gap-2.5"
                >
                  View Case Study <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div> */}
            </motion.article>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
